'use client';

import { Transition } from '@headlessui/react';
import { Trans, useTranslation } from 'next-i18next';
import { useBjForm } from '@hooks/useBjForm';
import { useCreditsCalculator } from '@hooks/credits/useCreditsCalculator';
import {
  Input, Button, FormError,
} from '@components/common/Forms';
import { TailwindSpinner } from '@components/common';
import React, { useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/16/solid';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { useSwrAccountDiscountData } from '@hooks/useSwrApi/endpoints/order/useSwrAccountDiscountData';
import { DisplayAmounts } from './DisplayAmounts';

/**
 * @description Interface for the discount calculator.
 */
interface DiscountCalculatorProps {
  triggerCartDetails: <Data, Arg>(data: Arg) => Promise<Data | void>,
  loadingNextStep: boolean,
  prevCreditsAmount?: number,
  targetAmount?: number,
  transitionProps?: object,
  closeModal?: () => void,
}

/**
 * @description Renders the discount calculator.
 * - `triggerCartDetails` - Function to call when the user clicks the submit button.
 * - `loadingNextStep` - Boolean to show loading state on the submit button.
 * - `prevCreditsAmount` - The previously set credits amount if available. Used to set the initial value when user goes back.
 * - `transitionProps` - Common transition props for the Transition component. If not provided, there will be no transition.
 * - `closeModal` - Optional function to close the modal.
 */
export const DiscountCalculator = (props: DiscountCalculatorProps) => {
  // Destructure props
  const {
    triggerCartDetails,
    loadingNextStep,
    prevCreditsAmount,
    targetAmount,
    transitionProps,
  } = props;

  // Locale and account discount data (to get nextDiscountAmount)
  const { locale } = useExtendedRouter();
  const { data: accountDiscountData } = useSwrAccountDiscountData({ locale, fetchCondition: true });

  // Input constraints mirror useCreditsCalculator defaults for initial clamping
  const INPUT_MIN = 1;
  const INPUT_MAX = 200000;

  // Initial credits amount
  const fallbackInitial = useMemo(() => {
    const next = accountDiscountData?.nextDiscountAmount;
    return typeof next === 'number' ? Math.round(next) : undefined;
  }, [accountDiscountData?.nextDiscountAmount]);

  // If a targetAmount is provided and we know last year's spend, set default so lastYear + input = target
  const computedFromTarget = useMemo(() => {
    if (typeof targetAmount !== 'number') return undefined;
    type WithLastYear = { lastYearOrderValueEur?: number };

    const lastYearMaybe = (accountDiscountData as unknown as WithLastYear)?.lastYearOrderValueEur;
    if (typeof lastYearMaybe !== 'number') return undefined;

    const lastYear = Math.round(lastYearMaybe);
    const desiredRaw = Math.max(0, Math.round(targetAmount - lastYear));
    return Math.max(INPUT_MIN, Math.min(INPUT_MAX, desiredRaw));
  }, [targetAmount, accountDiscountData]);

  const initialCreditsAmount = targetAmount ?? computedFromTarget ?? prevCreditsAmount ?? fallbackInitial;

  // Translations
  const { t } = useTranslation('common');

  // Credit calculator hook
  const {
    minValue,
    maxValue,
    isValid,
    creditsAmount,
    getCreditsAmount,
    priceData,
    priceIsValidating,
  } = useCreditsCalculator(
    initialCreditsAmount,
  );

  const {
    record,
    handleCallbackSubmit,
  } = useBjForm();
  const [showDiscount, setShowDiscount] = useState(false);

  const isDiscountEligible = accountDiscountData?.accountDiscountPercentage !== undefined && accountDiscountData?.accountDiscountPercentage > 0
    && accountDiscountData?.amountDiscountThreshold !== undefined && creditsAmount >= accountDiscountData?.amountDiscountThreshold;

  const totalConsumption = creditsAmount + Math.round(priceData?.lastYearOrderValueEur ?? 0);

  // Pending top-up summary
  const pendingTopUp = Math.round(accountDiscountData?.pendingTopUpAmount ?? 0);
  const totalPayableAmount = creditsAmount + (pendingTopUp > 0 ? pendingTopUp : 0);

  // Render component
  // ********************************
  return (
    <div className="min-w-full md:min-w-[600px]">
      {/* Neuro calculator */}
      <form
        noValidate
        onSubmit={(event) => handleCallbackSubmit(event, triggerCartDetails)}
      >
        {/* Form content */}
        <Transition
          as="div"
          className="max-w-full px-6 py-4"
          {...transitionProps}
        >

          {/* Input amount */}
          <label htmlFor="creditAmount" className="w-full">
            <span className="ml-2 font-bold text-ink-medium">
              {' '}
              {t('neuro.label.simple.amount')}
            </span>
            {/* input element */}
            <Input
              className="asOptional mt-0.5"
              type="number"
              min={minValue}
              max={maxValue}
              onChange={getCreditsAmount}
              {...record('creditAmount', { convertEmptyValue: 'toNumber', defaultValue: initialCreditsAmount })}
            />

            {/* input error element */}
            <FormError
              errorMessage={t('error.message.not_in_range', { min: minValue, max: maxValue })}
              isShowing={!isValid}
            />
          </label>

          {isDiscountEligible && (
            <div className="mt-2 rounded-md bg-surface-100 p-2">
              <span className="text-sm font-thin">
                <Trans
                  i18nKey="wallet.top_up.receive_x_percent_discount_y_amount"
                  values={{
                    percent: accountDiscountData?.accountDiscountPercentage,
                    amount: Math.floor((creditsAmount * (accountDiscountData?.accountDiscountPercentage ?? 0)) / 100),
                  }}
                  components={{ span: <span className="font-bold text-secondary" /> }}
                />
              </span>
            </div>
          )}

          {/* Show prices */}
          <span className="ml-2 text-sm text-ink-medium">
            {t('neuro.label.order-amount-no-discount')}
          </span>

          <div className="my-2 border-y border-dashed py-2">
            {/* total price */}
            {pendingTopUp > 0 && (
              <DisplayAmounts
                label={t('page.offer.calculator.pending_topup.euro')}
                isLoading={priceIsValidating}
                amount={pendingTopUp}
              />
            )}
            <DisplayAmounts
              label={t('page.offer.calculator.total_net.euro')}
              isLoading={priceIsValidating}
              amount={pendingTopUp > 0 ? Math.round(totalPayableAmount) : priceData?.priceToPay}
            />
          </div>

          { // If loading, show spinner, else show amount or 0
            priceIsValidating
              ? <TailwindSpinner className="size-4 text-ink-medium" />
              : (
                <div className="rounded-md bg-surface-100 p-2">

                  <div className="grid grid-cols-2 gap-2 py-1 text-sm font-thin">
                    <div>{t('price.calculator.euro.buy.history')}</div>
                    <div className="text-right">{((priceData?.lastYearOrderValueEur ?? 0) - pendingTopUp)?.toFixed(0)}</div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 py-1 text-sm font-thin">
                    <div>{t('price.calculator.euro.buy.total')}</div>
                    <div className="text-right">
                      {totalConsumption}
                    </div>
                  </div>

                  {priceData?.currentDiscountPercentage != null && (
                    <div className="grid grid-cols-2 gap-2 py-1 text-sm font-thin">
                      <div>{t('price.calculator.euro.current.discount')}</div>
                      <div className="text-right">
                        {priceData?.currentDiscountPercentage}
                        %
                      </div>
                    </div>
                  )}

                  {priceData?.nextDiscountPercentage != null && (
                  <div className="grid grid-cols-2 gap-2 py-1 text-sm font-thin">
                    <div>{t('price.calculator.euro.new.discount')}</div>
                    <div className="text-right">
                      {priceData?.nextDiscountPercentage}
                      %
                    </div>
                  </div>
                  )}

                  {priceData?.currentDiscountLevelExpirationDate
                  && (
                  <div className="grid grid-cols-2 gap-2 py-1 text-sm font-thin">
                    <div>{t('price.calculator.new.discount.available')}</div>
                    <div className="text-right">
                      {priceData?.currentDiscountLevelExpirationDate}
                    </div>
                  </div>
                  )}
                </div>
              )
          }


          <div className="text-right text-xs italic text-ink-medium">{t('page.offer.calculator.last.12.months')}</div>

        </Transition>

        {/* Submit button */}
        <div className="border-t px-6 py-4 text-right text-sm">
          <Button
            type="submit"
            rounding="full"
            color="secondary"
            size="sm"
            className="w-full !px-7"
            disabled={!(creditsAmount >= minValue && creditsAmount <= maxValue)}
            isLoading={loadingNextStep}
          >
            {t('order.choosePayment.title')}
          </Button>
        </div>

        <div
          role="presentation"
          onClick={() => setShowDiscount((prev) => !prev)}
          className="flex cursor-pointer items-center justify-center gap-2 pb-5 pt-2 text-center text-sm text-primary underline"
        >
          {t('page.offer.calculator.see.discounts')}
          {showDiscount ? <ChevronUpIcon className="size-5" /> : <ChevronDownIcon className="size-5" />}
        </div>

        {showDiscount && priceData?.discountLevels && priceData?.discountLevels.map((level) => (
          <div key={`${level.minAmount}-amount`} className="mb-2 grid grid-cols-3 border-b px-6 py-1">
            <div className="col-span-2">
              ≥
              {level.minAmount}
            </div>
            <div className="text-right">
              {level.percentage}
              %
            </div>
          </div>
        ))}

      </form>
    </div>
  );
};
